import type { EventType } from '@graphql/generated-base';
import { CatalogTreeRootInput } from '@graphql/generated-base';
import { deepmerge } from '@shared/utils/deepmerge';

import type { ValuesPerEnvironment } from './configEnvironment';
import { extractConfigValuesBasedOnEnvironment } from './configEnvironment';
import { BABY_SHOP_PATH, CATALOG_PATH } from '@apps/registry/common/components/Catalog/Catalog.constants';
import type { GetTranslation } from '@shared/core/i18n/useTranslation';
import type { TranslationsByNamespace } from '@locales/generated';

/**
 * Resolved text configuration for a custom block.
 */
export type CustomBlockTextConfig = Readonly<{
  componentTitle: string;
}>;

/**
 * Resolved configuration for all custom blocks, keyed by block type.
 * Extensible for future blocks (schedule, faq, etc.)
 */
export type WebsiteEditorCustomBlocksConfig = Readonly<{
  vip: CustomBlockTextConfig;
  faq: CustomBlockTextConfig;
}>;

export interface EventTypeConfig {
  eventDashboardEnabled: boolean;
  exampleConfigUsagePhoto: string;
  exampleConfigUsageDefaultCatalogTreeRoot: CatalogTreeRootInput;
  registryGiftCountAdvisorEnabled: boolean;
  registryDashboardLinkEnabled: boolean;
  checklistInGiftBasketWidgetEnabled: boolean;
  registryOverviewOverviewBillboardSliderEnabled: boolean;
  registryOverviewChecklistEnabled: boolean;
  registryOverviewGiftCountReg101Enabled: boolean;
  registryOverviewOverviewCarouselEnabled: boolean;
  registryOverviewRecommendedProductsSectionEnabled: boolean;
  registryOverviewJoyQuickAddButtonImageUrl: string;
  registryMobileAppEnabled: boolean;
  registryOverviewDepartmentShowcaseEnabled: boolean;
  registryQuickAddVideo2: string;
  registryQuickAddVideo3: string;
  registryAdminManageLink: string;
  registryAdminShopLink: string;
  registryOnlyEnableLinkingExternalRegistries: boolean;
  registryOverviewFAQs: boolean;
  registryPerks: boolean;
  universalRegistryToolsV2: boolean;
  registryConsultation: boolean;
  overviewBillboardCurationsEnabled: boolean;
  registryOverviewProductRecommendations: boolean;
  /**
   * Whether the user can add external registries.
   */
  externalRegistriesEnabled: boolean;
  externalRegistryHelpLink: string;
  impactRadiusCampaignId: number;
  impactRadiusActionTrackerId: number;
  impactRadiusCreateEventActionTrackerId: number;
  bottomCategoryShowcase: boolean;
  /**
   * Whether the user can see the subcategory highlights.
   */
  subCatHighlights: boolean;
  /**
   * Guest site SEO data
   */
  guestSitePageTitle: (
    eventDisplayName: string,
    userInfo: { partnerOneFirstName: string; partnerOneLastName: string; partnerTwoFirstName: string },
    t: GetTranslation<TranslationsByNamespace['websitePages']>,
  ) => string;
  guestSitePageDescription: (
    eventDisplayName: string,
    userInfo: { partnerOneFirstName: string; partnerOneLastName: string; partnerTwoFirstName: string },
    keywords: string,
    t: GetTranslation<TranslationsByNamespace['websitePages']>,
  ) => string;
  /**
   * Admin accommodations config
   */
  requireAccommodationsIntro: boolean;
}

type ConfigPerEventType = {
  [K in EventType]: Partial<ValuesPerEnvironment<EventTypeConfig>>;
};

const baseEventTypeConfig: EventTypeConfig = {
  eventDashboardEnabled: true,
  exampleConfigUsagePhoto: 'https://withjoy.com/assets/public/marcom-prod/registry-v2/registry-hero/registry-hero-static-v3.jpg?opt=aggressive&ver=2',
  exampleConfigUsageDefaultCatalogTreeRoot: CatalogTreeRootInput.default,
  registryDashboardLinkEnabled: true,
  registryGiftCountAdvisorEnabled: true,
  checklistInGiftBasketWidgetEnabled: false,
  registryOverviewOverviewBillboardSliderEnabled: true,
  registryOverviewChecklistEnabled: true,
  registryOverviewGiftCountReg101Enabled: false,
  registryOverviewOverviewCarouselEnabled: true,
  registryOverviewRecommendedProductsSectionEnabled: false,
  registryOverviewJoyQuickAddButtonImageUrl: 'https://withjoy.com/assets/public/apps/registry/overview/joy-button@3x.jpg',
  registryMobileAppEnabled: true,
  registryOverviewDepartmentShowcaseEnabled: false,
  externalRegistriesEnabled: true,
  registryQuickAddVideo2: 'https://withjoy.com/assets/public/apps/registry/quick-add/AddButton_Chrome_Animation2_v3_enhanced.webm',
  registryQuickAddVideo3: 'https://withjoy.com/assets/public/apps/registry/quick-add/AddButton_Chrome_Animation3_v3_enhanced.webm',
  registryAdminManageLink: '/edit/registry',
  registryAdminShopLink: CATALOG_PATH,
  registryOnlyEnableLinkingExternalRegistries: false,
  registryOverviewFAQs: false,
  registryPerks: false,
  universalRegistryToolsV2: false,
  registryConsultation: false,
  externalRegistryHelpLink: 'https://help.withjoy.com/knowledge-base/adding-an-external-registry-to-joy',
  impactRadiusCampaignId: 13168,
  impactRadiusActionTrackerId: 24458,
  impactRadiusCreateEventActionTrackerId: 24457,
  overviewBillboardCurationsEnabled: true,
  registryOverviewProductRecommendations: false,
  bottomCategoryShowcase: false,
  subCatHighlights: false,
  guestSitePageTitle: (
    eventDisplayName: string,
    _userInfo: { partnerOneFirstName: string; partnerOneLastName: string; partnerTwoFirstName: string },
    t: GetTranslation<TranslationsByNamespace['websitePages']>,
  ) => {
    const eventName = eventDisplayName.trim();
    return eventName ? t('seo', 'pageTitle', 'withEventName')({ eventName }) : t('seo', 'pageTitle', 'default')();
  },
  guestSitePageDescription: (
    eventDisplayName: string,
    _userInfo: { partnerOneFirstName: string; partnerOneLastName: string; partnerTwoFirstName: string },
    keywords: string,
    t: GetTranslation<TranslationsByNamespace['websitePages']>,
  ) => {
    const eventName = eventDisplayName.trim();
    return eventName ? t('seo', 'pageDescription', 'welcomeWithEvent')({ eventName, keywords }) : t('seo', 'pageDescription', 'welcomeDefault')({ keywords });
  },
  requireAccommodationsIntro: false,
};

const configPerEventType: ConfigPerEventType = {
  general: {
    registryDashboardLinkEnabled: false,
  },
  wedding: {
    eventDashboardEnabled: true,
    exampleConfigUsagePhoto: 'https://withjoy.com/assets/public/marcom-prod/registry-v2/zero-fee-cash-gifting/image-zero-fee-2.jpg?opt=aggressive&ver=2',
    exampleConfigUsageDefaultCatalogTreeRoot: CatalogTreeRootInput.curations,
    checklistInGiftBasketWidgetEnabled: true,
    registryQuickAddVideo2: 'https://withjoy.com/assets/public/apps/registry/quick-add/AddButton_Chrome_Animation2_v3_enhanced.webm',
    registryQuickAddVideo3: 'https://withjoy.com/assets/public/apps/registry/quick-add/AddButton_Chrome_Animation3_v3_enhanced.webm',
    registryOverviewFAQs: true,
    registryPerks: true,
    universalRegistryToolsV2: true,
    registryConsultation: true,
    registryOverviewProductRecommendations: true,
    bottomCategoryShowcase: true,
    subCatHighlights: true,
    guestSitePageTitle: (
      eventDisplayName: string,
      userInfo: { partnerOneFirstName: string; partnerOneLastName: string; partnerTwoFirstName: string },
      t: GetTranslation<TranslationsByNamespace['websitePages']>,
    ) => {
      const trimmedName = eventDisplayName.trim();
      let title = trimmedName;
      if (!trimmedName) {
        if (userInfo.partnerOneFirstName && userInfo.partnerTwoFirstName) {
          return t(
            'seo',
            'pageTitle',
            'withPartnerNames',
          )({
            partnerOne: userInfo.partnerOneFirstName,
            partnerTwo: userInfo.partnerTwoFirstName,
          });
        } else if (userInfo.partnerOneFirstName && userInfo.partnerOneLastName) {
          title = userInfo.partnerOneFirstName + ' ' + userInfo.partnerOneLastName;
        } else if (userInfo.partnerOneFirstName) {
          title = userInfo.partnerOneFirstName;
        }
      }
      return title ? t('seo', 'pageTitle', 'withEventName')({ eventName: title }) : t('seo', 'pageTitle', 'default')();
    },
    guestSitePageDescription: (
      eventDisplayName: string,
      userInfo: { partnerOneFirstName: string; partnerOneLastName: string; partnerTwoFirstName: string },
      keywords: string,
      t: GetTranslation<TranslationsByNamespace['websitePages']>,
    ) => {
      const trimmedName = eventDisplayName.trim();
      let title = '';
      if (userInfo.partnerOneFirstName && userInfo.partnerTwoFirstName) {
        return t(
          'seo',
          'pageDescription',
          'welcomeWithPartnerNames',
        )({
          partnerOne: userInfo.partnerOneFirstName,
          partnerTwo: userInfo.partnerTwoFirstName,
          keywords,
        });
      } else if (trimmedName) {
        title = trimmedName;
      } else if (userInfo.partnerOneFirstName && userInfo.partnerOneLastName) {
        title = userInfo.partnerOneFirstName + ' ' + userInfo.partnerOneLastName;
      } else if (userInfo.partnerOneFirstName) {
        title = userInfo.partnerOneFirstName;
      }
      return title ? t('seo', 'pageDescription', 'welcomeWithNames')({ names: title, keywords }) : t('seo', 'pageDescription', 'welcomeDefault')({ keywords });
    },
    requireAccommodationsIntro: true,
  },
  babyRegistry: {
    eventDashboardEnabled: false,
    exampleConfigUsagePhoto: 'https://thumbor.forbes.com/thumbor/fit-in/x/https://www.forbes.com/health/wp-content/uploads/2022/03/baby-eating-food.jpeg.jpg',
    exampleConfigUsageDefaultCatalogTreeRoot: {
      dev: CatalogTreeRootInput.default,
      stage: CatalogTreeRootInput.curations,
      prod: CatalogTreeRootInput.default,
    },
    checklistInGiftBasketWidgetEnabled: true,
    registryDashboardLinkEnabled: false,
    registryGiftCountAdvisorEnabled: false,
    registryOverviewOverviewBillboardSliderEnabled: false,
    registryOverviewOverviewCarouselEnabled: false,
    registryOverviewRecommendedProductsSectionEnabled: true,
    registryOverviewJoyQuickAddButtonImageUrl: 'https://withjoy.com/assets/public/apps/registry/overview/joy-quick-add@3x.jpg',
    registryMobileAppEnabled: false,
    registryOverviewDepartmentShowcaseEnabled: true,
    externalRegistriesEnabled: false,
    registryQuickAddVideo2: 'https://withjoy.com/assets/public/apps/registry/quick-add/AddButton_Chrome_Baby_Animation2_v1.mp4',
    registryQuickAddVideo3: 'https://withjoy.com/assets/public/apps/registry/quick-add/AddButton_Chrome_Baby_Animation3_v1.mp4',
    registryAdminManageLink: '/edit/registry/manage',
    registryAdminShopLink: BABY_SHOP_PATH,
    registryOnlyEnableLinkingExternalRegistries: false,
    externalRegistryHelpLink: 'https://help.withjoy.com/baby-registry/adding-external-baby-registries-to-joy',
    impactRadiusCampaignId: 21345,
    impactRadiusActionTrackerId: 42057,
    impactRadiusCreateEventActionTrackerId: 42056,
    overviewBillboardCurationsEnabled: false,
    guestSitePageTitle: (
      eventDisplayName: string,
      _userInfo: { partnerOneFirstName: string; partnerOneLastName: string; partnerTwoFirstName: string },
      t: GetTranslation<TranslationsByNamespace['websitePages']>,
    ) => {
      const eventName = eventDisplayName.trim();
      return eventName ? t('seo', 'pageTitle', 'babyRegistry')({ eventName }) : t('seo', 'pageTitle', 'babyRegistryDefault')();
    },
    guestSitePageDescription: (
      eventDisplayName: string,
      _userInfo: { partnerOneFirstName: string; partnerOneLastName: string; partnerTwoFirstName: string },
      _keywords: string,
      t: GetTranslation<TranslationsByNamespace['websitePages']>,
    ) => {
      const eventName = eventDisplayName.trim();
      return eventName ? t('seo', 'pageDescription', 'babyRegistryWithEvent')({ eventName }) : t('seo', 'pageDescription', 'babyRegistryDefault')();
    },
  },
  card: {},
};

let builtConfigs: Record<EventType, EventTypeConfig>;
export function getEventTypeConfig(eventType: EventType): EventTypeConfig {
  if (!builtConfigs) {
    builtConfigs = {} as Record<EventType, EventTypeConfig>;
    const baseConfig = extractConfigValuesBasedOnEnvironment<EventTypeConfig>(baseEventTypeConfig);
    Object.entries(configPerEventType).forEach(([eventType, eventTypeConfig]) => {
      builtConfigs[eventType as EventType] = deepmerge(baseConfig, extractConfigValuesBasedOnEnvironment<Partial<EventTypeConfig>>(eventTypeConfig));
    });
  }

  return builtConfigs[eventType];
}
